
import React, { useState, useEffect, useCallback } from 'react';
import { CircuitMode, ComponentState } from './types';
import CircuitView from './components/CircuitView';
import TheoryPanel from './components/TheoryPanel';
import TutorChat from './components/TutorChat';

const App: React.FC = () => {
  const [mode, setMode] = useState<CircuitMode>(CircuitMode.SERIES);
  const [state, setState] = useState<ComponentState>({
    isClosed: false,
    voltage: 9,
    resistance1: 10,
    resistance2: 10,
  });

  const toggleSwitch = () => setState(prev => ({ ...prev, isClosed: !prev.isClosed }));
  
  const handleParamChange = (key: keyof ComponentState, value: number) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50">
      {/* Sidebar Controls */}
      <aside className="w-full md:w-80 bg-white border-r border-slate-200 p-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-blue-600 mb-6">电路实验室</h1>
        
        <div className="space-y-6">
          <section>
            <label className="block text-sm font-semibold text-slate-700 mb-2">电路模式</label>
            <div className="flex bg-slate-100 p-1 rounded-lg">
              <button 
                onClick={() => setMode(CircuitMode.SERIES)}
                className={`flex-1 py-2 rounded-md transition-all ${mode === CircuitMode.SERIES ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
              >
                串联
              </button>
              <button 
                onClick={() => setMode(CircuitMode.PARALLEL)}
                className={`flex-1 py-2 rounded-md transition-all ${mode === CircuitMode.PARALLEL ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
              >
                并联
              </button>
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <label className="text-sm font-medium">电源电压 (V)</label>
                <span className="text-blue-600 font-mono">{state.voltage}V</span>
              </div>
              <input 
                type="range" min="1" max="24" step="1" 
                value={state.voltage} 
                onChange={(e) => handleParamChange('voltage', parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-1">
                <label className="text-sm font-medium">电阻 R1 (Ω)</label>
                <span className="text-blue-600 font-mono">{state.resistance1}Ω</span>
              </div>
              <input 
                type="range" min="1" max="50" step="1" 
                value={state.resistance1} 
                onChange={(e) => handleParamChange('resistance1', parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <label className="text-sm font-medium">电阻 R2 (Ω)</label>
                <span className="text-blue-600 font-mono">{state.resistance2}Ω</span>
              </div>
              <input 
                type="range" min="1" max="50" step="1" 
                value={state.resistance2} 
                onChange={(e) => handleParamChange('resistance2', parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </section>

          <button 
            onClick={toggleSwitch}
            className={`w-full py-3 rounded-xl font-bold transition-all ${state.isClosed ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-green-500 text-white hover:bg-green-600'}`}
          >
            {state.isClosed ? '断开开关' : '闭合开关'}
          </button>
        </div>
      </aside>

      {/* Main Simulation Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 p-4 md:p-8 flex items-center justify-center bg-white relative">
           <div className="absolute top-4 left-4 bg-slate-100/50 backdrop-blur-sm p-3 rounded-lg border border-slate-200 z-10">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">实时仿真</p>
              <p className="text-lg font-mono font-bold text-slate-800">
                {mode === CircuitMode.SERIES ? '串联回路' : '并联回路'}
              </p>
           </div>
           <CircuitView mode={mode} state={state} />
        </div>
        
        <div className="h-1/3 bg-slate-50 border-t border-slate-200 p-6 overflow-y-auto">
          <TheoryPanel mode={mode} state={state} />
        </div>
      </main>

      {/* AI Tutor Sidebar */}
      <section className="w-full md:w-96 bg-white border-l border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200 bg-blue-50/50">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            AI 助教
          </h2>
          <p className="text-xs text-slate-500">你可以问关于电路的任何问题</p>
        </div>
        <TutorChat />
      </section>
    </div>
  );
};

export default App;
